import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

// Wraps a card cover and plays a muted, looping preview when the pointer rests
// on it for `delayMs` (default 0.5s). The source is resolved lazily on first
// hover via `resolve()` -> { url, hls } so we never fetch presigned URLs or
// spin up HLS for cards the viewer just skims past. Reverts to the still cover
// (the wrapped children) on mouse-out. Used for trip clips and movie cards.
export default function HoverVideo({
  resolve,
  disabled = false,
  className = '',
  style,
  children,
  onClick,
  startSec = 0,
  delayMs = 500,
}) {
  const ref = useRef(null);
  const hlsRef = useRef(null);
  const timerRef = useRef(null);
  const hoverRef = useRef(false);
  const [src, setSrc] = useState(null);
  const [show, setShow] = useState(false);

  const stop = () => {
    hoverRef.current = false;
    clearTimeout(timerRef.current);
    setShow(false);
    const v = ref.current;
    if (v) {
      try {
        v.pause();
      } catch {
        /* noop */
      }
    }
  };

  const onEnter = () => {
    if (disabled) return;
    hoverRef.current = true;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (!hoverRef.current) return;
      if (src) {
        const v = ref.current;
        if (v) {
          try {
            v.currentTime = startSec;
          } catch {
            /* noop */
          }
          v.play().then(() => hoverRef.current && setShow(true)).catch(() => {});
        }
        return;
      }
      try {
        const s = await resolve();
        if (s && s.url && hoverRef.current) setSrc(s);
      } catch {
        /* ignore — keep the still cover */
      }
    }, delayMs);
  };

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    },
    []
  );

  // Attach the resolved source (hls.js for .m3u8, native otherwise) and play.
  useEffect(() => {
    const v = ref.current;
    if (!v || !src || !src.url) return undefined;
    let hls;
    const onLoaded = () => {
      try {
        v.currentTime = startSec;
      } catch {
        /* noop */
      }
      v.play()
        .then(() => {
          if (hoverRef.current) setShow(true);
          else v.pause();
        })
        .catch(() => {});
    };
    v.addEventListener('loadedmetadata', onLoaded);
    if (src.hls && !v.canPlayType('application/vnd.apple.mpegurl') && Hls.isSupported()) {
      hls = new Hls({ enableWorker: true, capLevelToPlayerSize: true });
      hlsRef.current = hls;
      hls.loadSource(src.url);
      hls.attachMedia(v);
    } else {
      v.src = src.url;
      v.load();
    }
    return () => {
      v.removeEventListener('loadedmetadata', onLoaded);
      if (hls) {
        hls.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, startSec]);

  return (
    <div className={`cs-hoverwrap ${className}`} style={style} onMouseEnter={onEnter} onMouseLeave={stop} onClick={onClick}>
      {children}
      {!disabled && (
        <video ref={ref} className={`cs-hover-video ${show ? 'is-on' : ''}`} muted loop playsInline preload="none" />
      )}
    </div>
  );
}
